import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Pagination } from './ui/pagination';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { 
  Plus, 
  Search, 
  Eye, 
  Edit, 
  Trash2, 
  FileText, 
  Calendar,
  Package,
  Filter,
  Download,
  Loader2,
  RefreshCw,
  FileCheck,
  CheckCircle,
  Warehouse
} from 'lucide-react';
import { grnApiService, GRN } from '../services/grnApi';
import { PermissionGate } from './ui/PermissionGate';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';


const GRNListingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedBranch } = useBranch();
  const [grns, setGrns] = useState<GRN[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  // Applied date filters (used for API calls)
  const [appliedStartDate, setAppliedStartDate] = useState<string>('');
  const [appliedEndDate, setAppliedEndDate] = useState<string>('');
  
  // Server-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Approval dialog state
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [grnToApprove, setGrnToApprove] = useState<GRN | null>(null);

  // Debounced search term for server-side filtering
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Refs to prevent duplicate API calls
  const isFetchingRef = useRef(false);
  const lastBranchIdRef = useRef<number | null>(null);
  const initialMountRef = useRef(true);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch GRNs from API with server-side pagination and search
  const fetchGRNs = useCallback(async (page: number = currentPage, search: string = debouncedSearchTerm) => {
    // Prevent duplicate concurrent calls
    if (isFetchingRef.current) {
      return;
    }

    // Resolve active branch id: selected branch from header overrides default
    // IMPORTANT: Always use selectedBranch if it's been set (not null)
    // Only fall back to defaultBranchId if selectedBranch hasn't been initialized
    const activeBranchId = selectedBranch !== null && selectedBranch !== undefined 
      ? selectedBranch 
      : user?.defaultBranchId;
    
    // Debug: log branch resolution (remove in production)
    if (activeBranchId) {
      if (selectedBranch !== null && selectedBranch !== undefined && selectedBranch === activeBranchId) {
        // console.log('[GRN] Using selectedBranch from header:', activeBranchId);
      } else {
        // console.log('[GRN] Using defaultBranchId:', activeBranchId, 'selectedBranch is:', selectedBranch);
      }
    }
    
    // Validate branch ID
    if (!activeBranchId) {
      setGrns([]);
      return;
    }

    // Don't fetch if we're already fetching for the same branchId, page, and search
    if (lastBranchIdRef.current === activeBranchId && !initialMountRef.current) {
      // Only skip if nothing else changed - let page and search changes through
      // This check will be handled by the useEffect dependencies
    }

    try {
      isFetchingRef.current = true;
      setLoading(true);
      
      lastBranchIdRef.current = activeBranchId;
      
      let response;
      
      // Use search API if there's a search term, otherwise use regular API
      if (search.trim()) {
        const searchResults = await grnApiService.searchGRNs(search.trim(), activeBranchId);
        // Convert search results to paginated response format
        response = {
          success: true,
          data: searchResults,
          pagination: {
            currentPage: 1,
            totalPages: 1,
            totalCount: searchResults.length,
            pageSize: searchResults.length,
            hasNext: false,
            hasPrevious: false
          }
        };
      } else {
        response = await grnApiService.getGRNs(
          activeBranchId,
          page,
          pageSize,
          undefined, // searchTerm
          statusFilter,
          appliedStartDate || undefined,
          appliedEndDate || undefined
        );
      }
      
      setGrns(response.data);
      setTotalCount(response.pagination.totalCount);
      setTotalPages(response.pagination.totalPages);
      
    } catch (error) {
      // console.error('Error fetching GRNs:', error);
      toast.error('Failed to fetch GRNs');
      setGrns([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
      initialMountRef.current = false;
    }
  }, [user?.defaultBranchId, selectedBranch, pageSize, statusFilter, appliedStartDate, appliedEndDate]);

  // Single effect to fetch data on branch/search/filter changes
  // Wait for user to be loaded before making any API calls
  useEffect(() => {
    // Don't fetch until user is loaded
    if (!user) {
      return;
    }

    // Always prioritize selectedBranch if it's been set (including 0)
    // Only use defaultBranchId if selectedBranch is null (hasn't been initialized yet)
    const activeBranchId = selectedBranch !== null && selectedBranch !== undefined 
      ? selectedBranch 
      : user?.defaultBranchId;
    
    // Don't fetch if we don't have a branch ID yet
    if (!activeBranchId) {
      return;
    }

    // Don't fetch if we're already fetching for the same branch
    if (isFetchingRef.current && lastBranchIdRef.current === activeBranchId) {
      return;
    }

    // IMPORTANT: If selectedBranch changed (user selected different branch in header),
    // we must fetch with the new branch, even if we just fetched
    const branchChanged = lastBranchIdRef.current !== null && lastBranchIdRef.current !== activeBranchId;
    
    // Reset page to 1 when branch changes
    if (branchChanged) {
      setCurrentPage(1);
    }

    // Fetch with the current activeBranchId (prioritizes selectedBranch over defaultBranchId)
    fetchGRNs(1, debouncedSearchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch, user?.defaultBranchId, debouncedSearchTerm, statusFilter, appliedStartDate, appliedEndDate, user]);

  // Handle page changes separately (when branch/search/filters haven't changed)
  useEffect(() => {
    if (!user) return;
    
    // Prioritize selectedBranch when explicitly set, otherwise use defaultBranchId
    const activeBranchId = selectedBranch !== null && selectedBranch !== undefined 
      ? selectedBranch 
      : user?.defaultBranchId;
    if (!activeBranchId) return;
    
    // Skip if this is the initial mount (handled by the effect above)
    if (initialMountRef.current) return;
    
    // Only fetch if page changed but branch/search/filters didn't change
    if (lastBranchIdRef.current === activeBranchId && !isFetchingRef.current) {
      fetchGRNs(currentPage, debouncedSearchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Refresh data
  const handleRefresh = async () => {
    await fetchGRNs();
  };

  const handleView = (grnId: number) => {
    navigate(`/grn/view/${grnId}`);
  };

  const handleEdit = (grnId: number) => {
    navigate(`/grn/edit/${grnId}`);
  };

  const handleEditStore = (grnId: number) => {
    navigate(`/grn/edit/${grnId}?mode=store`);
  };

  const handleDelete = async (grnId: number) => {
    const grn = grns.find(g => g.id === grnId);
    if (!grn) return;

    if (window.confirm(`Are you sure you want to delete GRN ${grn.grnNumber}?`)) {
      try {
        await grnApiService.deleteGRN(grnId.toString());
        toast.success('GRN deleted successfully');
        // Refresh the data after deletion
        await fetchGRNs();
      } catch (error) {
        // console.error('Error deleting GRN:', error);
        toast.error('Failed to delete GRN');
      }
    }
  };

  const handleAccessDoc = (grnId: number) => {
    navigate(`/grn/${grnId}/access-doc`);
  };

  const handleApproveClick = (grn: GRN) => {
    setGrnToApprove(grn);
    setApproveDialogOpen(true);
  };

  const handleApproveConfirm = async () => {
    if (!grnToApprove || !user?.id) {
      return;
    }

    try {
      await grnApiService.approveGRN(grnToApprove.id.toString(), user.id);
      setApproveDialogOpen(false);
      setGrnToApprove(null);
      // Refresh the data after approval
      await fetchGRNs();
    } catch (error) {
      // console.error('Error approving GRN:', error);
      // Error toast is already shown in the API service
    }
  };

  const handleDateFilterSearch = () => {
    // Apply the date filters and trigger search
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setCurrentPage(1); // Reset to first page
  };

  const handleClearDateFilter = () => {
    setStartDate('');
    setEndDate('');
    setAppliedStartDate('');
    setAppliedEndDate('');
    setCurrentPage(1);
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      
      // Get active branch ID
      const activeBranchId = selectedBranch !== null && selectedBranch !== undefined 
        ? selectedBranch 
        : user?.defaultBranchId;
      
      if (!activeBranchId) {
        toast.error('Please select a branch to export GRNs');
        return;
      }

      // Prepare status filter (exclude 'all')
      const statusParam = statusFilter !== 'all' ? statusFilter : undefined;

      // Call export API
      const response = await grnApiService.exportGRNs(
        activeBranchId,
        statusParam,
        appliedStartDate || undefined,
        appliedEndDate || undefined
      );

      if (!response.success || !response.data || response.data.length === 0) {
        toast.error('No data to export');
        return;
      }

      // Convert JSON data to CSV format
      const headers = [
        'GRN Number',
        'PO Number',
        'DC Number',
        'Supplier',
        'Vehicle Number',
        'Received Date',
        'Status'
      ];

      // Format date consistently
      const formatDate = (dateString: string | null | undefined): string => {
        if (!dateString) return 'N/A';
        try {
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return 'N/A';
          // Format as DD/MM/YYYY to match table display
          return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          });
        } catch {
          return 'N/A';
        }
      };

      // Escape CSV values properly
      const escapeCsvValue = (value: string | null | undefined): string => {
        if (value === null || value === undefined) return 'N/A';
        const str = String(value);
        // Escape quotes and wrap in quotes if contains comma, quote, or newline
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      const csvRows = [
        headers.join(','),
        ...response.data.map((grn) => {
          const row = [
            escapeCsvValue(grn.grnNumber),
            escapeCsvValue(grn.poHeader?.poNumber),
            escapeCsvValue(grn.dcNo),
            escapeCsvValue(grn.supplier?.supplierName),
            escapeCsvValue(grn.vehicleNumber),
            formatDate(grn.receiveDate),
            escapeCsvValue(grn.status)
          ];
          return row.join(',');
        })
      ];

      // Create CSV content with proper line endings
      const csvContent = csvRows.join('\r\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `GRN_Export_${timestamp}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${response.data.length} GRNs to CSV`);
    } catch (error) {
      // console.error('Error exporting GRNs:', error);
      // Error toast is already shown in the API service
    } finally {
      setLoading(false);
    }
  };


  // GRN management route (from backend permissions)
  const GRN_ROUTE = '/grn';
  const STORES_ROUTE = '/stores';

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
        <CardHeader className="bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center">
                <FileText className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-xl">Goods Received Notes</CardTitle>
                <p className="text-primary-100 text-sm">Manage and track all goods received notes</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleRefresh}
                variant="outline"
                size="sm"
                disabled={loading}
                className="bg-white/20 text-white border-white/30 hover:bg-white/30"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
              <Button
                onClick={handleExport}
                variant="outline"
                size="sm"
                disabled={loading}
                className="bg-white/20 text-white border-white/30 hover:bg-white/30"
                title="Export GRNs to CSV"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
              </Button>
              <PermissionGate route={GRN_ROUTE} action="create">
                <Button
                  onClick={() => navigate('/grn/new')}
                  className="bg-white text-primary-600 hover:bg-primary-50 shadow-lg"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create GRN
                </Button>
              </PermissionGate>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {/* Search and Filters */}
          <div className="flex items-center gap-4 mb-6 flex-wrap">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search GRNs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-primary-200 focus:border-primary-400 focus:ring-primary-400"
              />
            </div>
            <div className="flex items-center gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40 border-primary-200 focus:border-primary-400 focus:ring-primary-400">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="RECEIVED">Received</SelectItem>
                  <SelectItem value="APPROVED">Approved</SelectItem>
                  <SelectItem value="DRAFT">Draft</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Date Range Filter */}
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-400" />
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-40 border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                placeholder="Start Date"
              />
              <span className="text-gray-400">to</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-40 border-primary-200 focus:border-primary-400 focus:ring-primary-400"
                placeholder="End Date"
              />
              <Button
                variant="default"
                size="sm"
                onClick={handleDateFilterSearch}
                disabled={loading}
                className="bg-primary-600 text-white hover:bg-primary-700"
              >
                <Search className="h-4 w-4 mr-1" />
                Search
              </Button>
              {(startDate || endDate || appliedStartDate || appliedEndDate) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearDateFilter}
                  className="border-primary-200 text-primary-700 hover:bg-primary-50"
                >
                  Clear
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-primary-200 text-primary-700">
                {totalCount} GRNs
              </Badge>
            </div>
          </div>

          {/* GRN Table */}
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-primary-600" />
              <p className="text-gray-600">Loading GRNs...</p>
            </div>
          ) : (
            <div className="border border-primary-200 rounded-lg overflow-hidden bg-white">
              {grns.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                      <TableHead>GRN Number</TableHead>
                      <TableHead>PO Number</TableHead>
                      <TableHead>DC Number</TableHead>
                      <TableHead>Supplier</TableHead>
                      <TableHead>Received Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                <TableBody>
                  {grns.map((grn) => (
                    <TableRow key={grn.id}>
                                             <TableCell>
                         <div className="space-y-1">
                           <div className="font-medium text-gray-900">{grn.grnNumber}</div>
                           {/* <div className="text-xs text-gray-500">{grn.notes || 'No notes'}</div> */}
                         </div>
                       </TableCell>
                       <TableCell>
                         <span className="font-medium text-blue-600">{grn.poHeader?.poNumber || 'N/A'}</span>
                       </TableCell>
                       <TableCell>
                         <span className="font-medium text-gray-700">{grn.dcNo || 'N/A'}</span>
                       </TableCell>
                       <TableCell>
                         <div className="space-y-1">
                           <div className="font-medium">{grn.supplier?.supplierName || 'N/A'}</div>
                           <div className="text-xs text-gray-500">{grn.vehicleNumber}</div>
                         </div>
                       </TableCell>
                       <TableCell>
                         <div className="flex items-center gap-2">
                           <Calendar className="h-4 w-4 text-gray-400" />
                           <span>{new Date(grn.receiveDate).toLocaleDateString()}</span>
                         </div>
                       </TableCell>
                       <TableCell>
                         <Badge 
                           variant="outline"
                           className={
                             grn.status === 'APPROVED' 
                              ? 'border-green-500 text-green-700 bg-green-50'
                              : grn.status === 'RECEIVED'
                              ? 'border-blue-500 text-blue-700 bg-blue-50'
                              : grn.status === 'DRAFT'
                              ? 'border-gray-500 text-gray-700 bg-gray-50'
                              : 'border-yellow-500 text-yellow-700 bg-yellow-50'
                           }
                         >
                           {grn.status || 'N/A'}
                         </Badge>
                       </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center gap-2">
                          <PermissionGate route={GRN_ROUTE} action="view">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleView(grn.id)}
                              className="text-blue-600 hover:bg-blue-50 border-blue-200"
                              title="View GRN"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate route={STORES_ROUTE} action="edit">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditStore(grn.id)}
                              className="text-amber-700 hover:bg-amber-50 border-amber-300"
                              title="Edit store assignment"
                            >
                              <Warehouse className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate route={GRN_ROUTE} action="edit">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(grn.id)}
                              className="text-green-600 hover:bg-green-50 border-green-300"
                              title="Edit GRN"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate route={GRN_ROUTE} action="edit">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleAccessDoc(grn.id)}
                              className="text-purple-600 hover:bg-purple-50 border-purple-200"
                              title="Access Documents"
                            >
                              <FileCheck className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate route={GRN_ROUTE} action="approve">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleApproveClick(grn)}
                              disabled={grn.status === 'APPROVED'}
                              className="text-green-600 hover:bg-green-50 border-green-200 disabled:opacity-50 disabled:cursor-not-allowed"
                              title={grn.status === 'APPROVED' ? 'GRN already approved' : 'Approve GRN'}
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate route={GRN_ROUTE} action="delete">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(grn.id)}
                              className="text-red-600 hover:bg-red-50 border-red-200"
                              title="Delete GRN"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">No GRNs found</p>
                  <p className="text-gray-400">
                    {searchTerm || statusFilter !== 'all' || appliedStartDate || appliedEndDate
                      ? 'Try adjusting your filters'
                      : 'Create your first GRN to get started'
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-gray-600">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} GRNs
              </div>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Approval Confirmation Dialog */}
      <AlertDialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Approve GRN</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to approve GRN <strong>{grnToApprove?.grnNumber}</strong>? 
              This action will mark the GRN as approved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setGrnToApprove(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleApproveConfirm}
              className="bg-green-600 hover:bg-green-700"
            >
              Approve
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default GRNListingPage;
